import React from 'react'

import { Box } from 'ui/components/Box'
import {
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownSeparator,
    DropdownTrigger,
} from 'ui/components/Dropdown'
import { Tooltip } from 'ui/components/Tooltip'
import { stopPropagation } from 'ui/helpers/utilities'

import { useManageDataTabDropdown } from './hooks/useManageDataTabDropdownState'

type ManageDataTabDropdownProps = React.ComponentPropsWithoutRef<typeof DropdownTrigger> & {
    objectSid: string
    open: boolean
    onOpenChange: (open: boolean) => void
    onOpenImportModal: () => void
    onOpenPermissionsModal: () => void
    onOpenSettingsModal: () => void
    onOpenRenamePopup: () => void
}

export const ManageDataTabDropdown: React.FC<ManageDataTabDropdownProps> = ({
    children,
    objectSid,
    open,
    onOpenChange,
    onOpenImportModal: providedOnOpenImportModal,
    onOpenPermissionsModal: providedOnOpenPermissionsModal,
    onOpenSettingsModal: providedOnOpenSettingsModal,
    onOpenRenamePopup: providedOnOpenRenamePopup,
    ...props
}) => {
    const {
        onGoToTrash,
        handleOpenImportModal,
        allowImport,
        trashLabel,
        onExportData,
        handleOpenPermissionsModal,
        handleOpenSettingsModal,
        handleOpenSharingSettingsModal,
        allowShare,
        showShare,
        shareLabel,
        shareInfoText,
        allowDelete,
        deleteLabel,
        deleteInfoText,
        onDelete,
        onPointerDownOutside,
        handleOpenRenamePopup,
        allowRename,
        renameInfoText,
    } = useManageDataTabDropdown({
        objectSid,
        onOpenChange,
        onOpenImportModal: providedOnOpenImportModal,
        onOpenPermissionsModal: providedOnOpenPermissionsModal,
        onOpenSettingsModal: providedOnOpenSettingsModal,
        onOpenRenamePopup: providedOnOpenRenamePopup,
    })

    return (
        <Dropdown modal={false} open={open} onOpenChange={onOpenChange}>
            <DropdownTrigger asChild {...props}>
                {children}
            </DropdownTrigger>
            <DropdownContent
                style={{ width: '300px' }}
                onPointerDownOutside={onPointerDownOutside}
                onCloseAutoFocus={stopPropagation}
            >
                {allowImport && (
                    <DropdownItem
                        label="Import from .CSV"
                        startIcon={{ name: 'Import' }}
                        onSelect={handleOpenImportModal}
                    />
                )}
                <DropdownItem
                    label="Export data to .CSV"
                    startIcon={{ name: 'Download' }}
                    onSelect={onExportData}
                />
                <DropdownSeparator />
                <DropdownItem
                    label={
                        <Tooltip zIndex={9999} content={renameInfoText} pointerEvents="auto">
                            <Box>Rename table</Box>
                        </Tooltip>
                    }
                    startIcon={{ name: 'PencilLine' }}
                    disabled={!allowRename}
                    onSelect={handleOpenRenamePopup}
                />
                {showShare && (
                    <DropdownItem
                        label={
                            <Tooltip zIndex={9999} content={shareInfoText} pointerEvents="auto">
                                <Box>{shareLabel}</Box>
                            </Tooltip>
                        }
                        startIcon={{ name: 'Share2' }}
                        onSelect={handleOpenSharingSettingsModal}
                        disabled={!allowShare}
                    />
                )}
                <DropdownItem
                    label="Permissions"
                    startIcon={{ name: 'Lock' }}
                    onSelect={handleOpenPermissionsModal}
                />
                <DropdownItem
                    label="Settings"
                    startIcon={{ name: 'Settings' }}
                    onSelect={handleOpenSettingsModal}
                />
                <DropdownSeparator />
                <DropdownItem
                    label={trashLabel}
                    startIcon={{ name: 'Redo2' }}
                    onSelect={onGoToTrash}
                />
                <DropdownItem
                    label={
                        <Tooltip zIndex={9999} content={deleteInfoText} pointerEvents="auto">
                            <Box>{deleteLabel}</Box>
                        </Tooltip>
                    }
                    startIcon={{ name: 'Trash2' }}
                    variant="destructive"
                    disabled={!allowDelete}
                    onSelect={onDelete}
                />
            </DropdownContent>
        </Dropdown>
    )
}
